import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { buildApiUrl } from '../../utils/apiUrl';
import { supabase } from '../../supabase';
import { imprimirFormatoMuestreo } from './formatoMuestreoImprimir';
import { encolar, uuid } from '../../utils/offlineQueue';
import { ClipboardCheck, X, Save, Loader2, Printer } from 'lucide-react';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface Columna {
  key: string;
  label: string;
  grupo?: string;
  sub?: string;
  tipo: 'int' | 'dec' | 'escala';
  calc?: string;
  min?: number;
  max?: number;
}
interface TipoMuestreo {
  codigo: string;
  nombre: string;
  ancla: string | null;
  columnas_json: Columna[];
  regla_muestras_json: { tramos: { maxArea: number | null; muestras: number }[]; plantasPorMuestra: number } | null;
}
interface Seccion {
  lote_num: string;
  seccion: string;
  area_ha: number | null;
  plantas: number | null;
}
interface FilaCaptura extends Seccion {
  muestra: number | null;
  datos: Record<string, string>; // valores capturados (string para inputs)
}

// nº de muestras sugerido por área (regla del tipo)
function muestrasPorArea(regla: TipoMuestreo['regla_muestras_json'], area: number | null): { muestras: number; plantas: number } | null {
  if (!regla || area == null) return null;
  for (const t of regla.tramos) {
    if (t.maxArea == null || area < t.maxArea) {
      return { muestras: t.muestras, plantas: t.muestras * regla.plantasPorMuestra };
    }
  }
  return null;
}

export function ModalCapturaMuestreo({
  muestreoId, empresaId, usuario, onClose, onSaved, onError,
}: {
  muestreoId: number;
  empresaId: number;
  usuario: string;
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [tipo, setTipo] = useState<TipoMuestreo | null>(null);
  const [muestreo, setMuestreo] = useState<any>(null);
  const [filas, setFilas] = useState<FilaCaptura[]>([]);
  const [observaciones, setObservaciones] = useState('');
  // Móvil: < 900px → vista de formulario por sección (1 a la vez). Escritorio/tablet → tabla.
  const [esMovil, setEsMovil] = useState(typeof window !== 'undefined' && window.innerWidth < 900);
  const [seccionActiva, setSeccionActiva] = useState(0);
  useEffect(() => {
    const onResize = () => setEsMovil(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/programa/${muestreoId}/captura?empresa_id=${empresaId}`));
      const j = await res.json();
      if (!j.ok) { onError(j.error || 'Error al cargar'); onClose(); return; }
      setTipo(j.tipo);
      setMuestreo(j.muestreo);
      setObservaciones(j.muestreo?.observaciones || '');

      // Construir filas: capturas previas o secciones precargadas
      const cols: Columna[] = j.tipo?.columnas_json || [];
      const previas: any[] = j.capturas || [];
      if (previas.length > 0) {
        setFilas(previas.map((cap) => ({
          lote_num: cap.lote_num, seccion: cap.seccion, area_ha: cap.area_ha,
          plantas: cap.plantas, muestra: cap.muestra,
          datos: Object.fromEntries(cols.map(c => [c.key, String(cap.datos_json?.[c.key] ?? '')])),
        })));
      } else {
        setFilas((j.secciones || []).map((s: Seccion) => {
          const sug = muestrasPorArea(j.tipo?.regla_muestras_json, s.area_ha);
          return {
            ...s,
            muestra: sug?.plantas ?? null,
            datos: Object.fromEntries(cols.map(c => [c.key, ''])),
          };
        }));
      }
    } catch (e: any) {
      onError(e?.message || 'Error de red'); onClose();
    } finally {
      setCargando(false);
    }
  }, [muestreoId, empresaId, onClose, onError]);

  useEffect(() => { cargar(); }, [cargar]);

  const cols = tipo?.columnas_json || [];
  // Agrupar columnas por su "grupo" para la cabecera de 2 niveles
  const grupos = useMemo(() => {
    const orden: string[] = [];
    const map = new Map<string, Columna[]>();
    for (const c of cols) {
      const g = c.grupo || '';
      if (!map.has(g)) { map.set(g, []); orden.push(g); }
      map.get(g)!.push(c);
    }
    return orden.map(g => ({ grupo: g, cols: map.get(g)! }));
  }, [cols]);

  const setDato = (fi: number, key: string, val: string) =>
    setFilas(fs => fs.map((f, i) => (i === fi ? { ...f, datos: { ...f.datos, [key]: val } } : f)));
  const setMuestra = (fi: number, val: string) =>
    setFilas(fs => fs.map((f, i) => (i === fi ? { ...f, muestra: val === '' ? null : Number(val) } : f)));

  // Total por columna (suma de todas las filas)
  const totalCol = (key: string) =>
    filas.reduce((s, f) => s + (Number(f.datos[key]) || 0), 0);

  // Imprimir el formato de campo (con lo que esté en pantalla: capturado o en blanco).
  const imprimir = async () => {
    const { data } = await supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle();
    imprimirFormatoMuestreo({
      muestreo,
      tipoCodigo: tipo?.codigo || '',
      tipoNombre: tipo?.nombre || muestreo?.labor_nombre || 'Muestreo',
      ancla: tipo?.ancla ?? null,
      cols,
      grupos,
      filas,
      observaciones,
      usuario,
      empresaNombre: data?.nombre || '',
    });
  };

  // Navegación tipo Excel: Enter/flechas mueven el foco entre celdas.
  // Cada input lleva data-fila y data-col (col -1 = "Muestra", 0..n = columnas del tipo).
  const totalFilas = filas.length;
  const totalCols = cols.length; // 0..totalCols-1 ; -1 es Muestra
  const enfocar = (fila: number, col: number) => {
    if (fila < 0 || fila >= totalFilas || col < -1 || col >= totalCols) return;
    const el = document.querySelector<HTMLInputElement>(
      `input[data-fila="${fila}"][data-col="${col}"]`
    );
    if (el) { el.focus(); el.select(); }
  };
  const onKeyCelda = (fila: number, col: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'Enter':
      case 'ArrowDown':
        e.preventDefault();
        // Si está en la última fila, salta a la primera de la siguiente columna.
        if (fila + 1 < totalFilas) enfocar(fila + 1, col);
        else enfocar(0, col + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        enfocar(fila - 1, col);
        break;
      case 'ArrowRight':
        // En input[type=number] selectionStart es null → mover siempre de celda.
        // Al pasar la última columna, salta al inicio de la fila siguiente.
        e.preventDefault();
        if (col + 1 < totalCols) enfocar(fila, col + 1);
        else if (fila + 1 < totalFilas) enfocar(fila + 1, -1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (col - 1 >= -1) enfocar(fila, col - 1);
        else if (fila - 1 >= 0) enfocar(fila - 1, totalCols - 1);
        break;
      default:
        break;
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const capturas = filas
        .map(f => {
          const datos_json: Record<string, number> = {};
          for (const c of cols) {
            const v = f.datos[c.key];
            if (v !== '' && v != null) datos_json[c.key] = Number(v);
          }
          return {
            lote_num: f.lote_num, seccion: f.seccion, area_ha: f.area_ha,
            plantas: f.plantas, muestra: f.muestra, datos_json,
          };
        })
        .filter(f => Object.keys(f.datos_json).length > 0);

      // Resumen: totales por columna (para resultado_json del muestreo)
      const resumen_json = cols.map(c => ({ clave: c.label, valor: String(totalCol(c.key)) }))
        .filter(x => Number(x.valor) > 0);

      const clientUuid = uuid();
      const ahora = new Date().toISOString();
      const payload = {
        tipo_codigo: tipo?.codigo,
        capturas,
        resumen_json,
        observaciones: observaciones.trim() || null,
        ejecutada_por: usuario,
        client_uuid: clientUuid,
        captura_origen: navigator.onLine ? 'web' : 'offline',
        registrado_offline_at: navigator.onLine ? null : ahora,
      };

      // Encola en IndexedDB para sincronizar después (campo sin señal).
      const guardarEnCola = async () => {
        await encolar({
          client_uuid: clientUuid,
          muestreoId,
          empresaId,
          labor_nombre: muestreo?.labor_nombre || 'Muestreo',
          payload: { ...payload, captura_origen: 'offline', registrado_offline_at: ahora },
          creado_at: ahora,
        });
        onSaved(`"${muestreo?.labor_nombre}" guardada sin señal — se sincronizará al recuperar la conexión.`);
      };

      // Sin señal → directo a la cola.
      if (!navigator.onLine) { await guardarEnCola(); return; }

      // Con señal → intentar enviar; si falla la red, cae a la cola.
      try {
        const res = await fetch(buildApiUrl(`/aplicaciones/labores/programa/${muestreoId}/captura?empresa_id=${empresaId}`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const j = await res.json();
        if (!j.ok) { onError(j.error || 'Error al guardar'); return; }
        onSaved(`"${muestreo?.labor_nombre}" registrada (${j.guardadas} secciones)`);
      } catch {
        await guardarEnCola();
      }
    } catch (e: any) {
      onError(e?.message || 'Error al preparar el guardado');
    } finally {
      setGuardando(false);
    }
  };

  const cellInput: React.CSSProperties = {
    width: '3.2rem', textAlign: 'center', background: 'var(--surface-deep)', outline: 'none',
    color: 'var(--ink)', border: '1px solid var(--line-dim)', borderRadius: 3, padding: '2px 3px', fontSize: '12px',
  };

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-[1000] flex justify-center ${esMovil ? '' : 'items-center p-3'}`} style={{ background: 'rgba(8,12,22,0.72)', backdropFilter: 'blur(2px)' }} onClick={onClose}>
      <div className={`flex flex-col overflow-hidden ${esMovil ? 'w-full h-full' : 'rounded-xl w-full max-w-[95vw]'}`}
        style={{
          background: 'var(--surface-base)',
          border: esMovil ? 'none' : '1px solid var(--emp-accent)',
          maxHeight: esMovil ? '100%' : '92vh',
          boxShadow: esMovil ? 'none' : '0 24px 60px rgba(0,0,0,0.55)',
        }}
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-3 flex items-center gap-3 shrink-0" style={{ borderBottom: '1px solid var(--line)' }}>
          <ClipboardCheck size={18} style={{ color: 'var(--emp-accent-txt)' }} />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold truncate" style={{ color: 'var(--ink)' }}>
              {muestreo?.labor_nombre} {tipo ? `· ${tipo.nombre}` : ''}
            </h2>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
              Grupo {muestreo?.grupo_forza} · Lotes {muestreo?.lote_num} · {filas.length} secciones
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {cargando ? (
            <div className="py-16 text-center"><Loader2 size={22} className="animate-spin inline" style={{ color: 'var(--ink-faint)' }} /></div>
          ) : !tipo ? (
            <div className="py-12 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
              Esta labor no tiene un formato de muestreo configurado.
            </div>
          ) : filas.length === 0 ? (
            <div className="py-12 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
              No se encontraron secciones/bloques para los lotes de este grupo de forza.
            </div>
          ) : esMovil ? (
            /* ── Vista MÓVIL: una sección por pantalla, campos apilados (touch) ── */
            <VistaMovil
              filas={filas}
              cols={cols}
              grupos={grupos}
              idx={Math.min(seccionActiva, filas.length - 1)}
              setIdx={setSeccionActiva}
              setDato={setDato}
              setMuestra={setMuestra}
              totalCol={totalCol}
            />
          ) : (
            <table style={{ borderCollapse: 'collapse', fontSize: '11px', width: '100%' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-raised)' }}>
                {/* Fila de grupos */}
                <tr>
                  <th rowSpan={2} style={thFijo}>Lote</th>
                  <th rowSpan={2} style={thFijo}>Sec.</th>
                  <th rowSpan={2} style={thFijo}>Área</th>
                  <th rowSpan={2} style={thFijo}>Plantas</th>
                  <th rowSpan={2} style={thFijo}>Muestra</th>
                  {grupos.map((g, gi) => (
                    <th key={gi} colSpan={g.cols.length} style={{ ...thGrupo, ...(g.grupo ? {} : { color: 'transparent' }) }}>
                      {g.grupo || '·'}
                    </th>
                  ))}
                </tr>
                {/* Fila de columnas */}
                <tr>
                  {cols.map((c) => (
                    <th key={c.key} style={thCol} title={c.label}>
                      {c.sub || (c.grupo ? c.label.replace(c.grupo, '').trim() || c.label : c.label)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filas.map((f, fi) => (
                  <tr key={fi} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <td style={tdFijo}>{f.lote_num}</td>
                    <td style={tdFijo}>{f.seccion}</td>
                    <td style={{ ...tdFijo, color: 'var(--ink-muted)' }}>{f.area_ha != null ? Number(f.area_ha).toFixed(2) : ''}</td>
                    <td style={{ ...tdFijo, color: 'var(--ink-muted)' }}>{f.plantas != null ? f.plantas.toLocaleString('es-CR') : ''}</td>
                    <td style={tdFijo}>
                      <input type="number" value={f.muestra ?? ''} onChange={(e) => setMuestra(fi, e.target.value)}
                        data-fila={fi} data-col={-1} onKeyDown={onKeyCelda(fi, -1)}
                        onFocus={(e) => e.currentTarget.select()}
                        style={{ ...cellInput, width: '3.6rem' }} />
                    </td>
                    {cols.map((c, ci) => (
                      <td key={c.key} style={{ padding: '2px 3px', textAlign: 'center' }}>
                        <input
                          type="number"
                          value={f.datos[c.key] ?? ''}
                          onChange={(e) => setDato(fi, c.key, e.target.value)}
                          data-fila={fi} data-col={ci} onKeyDown={onKeyCelda(fi, ci)}
                          onFocus={(e) => e.currentTarget.select()}
                          min={c.min} max={c.max}
                          style={cellInput}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Totales */}
                <tr style={{ borderTop: '2px solid var(--line)', fontWeight: 700 }}>
                  <td colSpan={5} style={{ ...tdFijo, textAlign: 'right', color: 'var(--ink-muted)' }}>Totales</td>
                  {cols.map((c) => (
                    <td key={c.key} style={{ padding: '4px 3px', textAlign: 'center', color: 'var(--emp-accent-txt)' }}>
                      {totalCol(c.key) || ''}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className={`px-4 py-3 shrink-0 ${esMovil ? 'flex flex-col gap-2' : 'flex items-center gap-3'}`} style={{ borderTop: '1px solid var(--line)' }}>
          <input
            type="text" value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Observaciones (opcional)…"
            className="flex-1 w-full text-sm rounded px-2 py-2 bg-transparent outline-none"
            style={{ color: 'var(--ink)', border: '1px solid var(--line)' }}
          />
          {!esMovil && (
            <span className="text-[11px] whitespace-nowrap" style={{ color: 'var(--ink-faint)' }}>Ejecuta: <b style={{ color: 'var(--ink-muted)' }}>{usuario}</b></span>
          )}
          <div className={`flex gap-2 ${esMovil ? 'w-full' : ''}`}>
            <button onClick={onClose} className={`px-3 py-2 rounded text-sm font-medium ${esMovil ? 'flex-1' : 'text-xs'}`} style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
            <button onClick={imprimir} disabled={cargando || !tipo || filas.length === 0} title="Imprimir formato"
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded font-medium ${esMovil ? 'flex-1 text-sm' : 'text-xs'}`}
              style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: (cargando || !tipo || filas.length === 0) ? 0.5 : 1 }}>
              <Printer size={13} /> {esMovil ? 'Imprimir' : 'Imprimir'}
            </button>
            <button onClick={guardar} disabled={guardando || cargando || !tipo}
              className={`flex items-center justify-center gap-1.5 px-4 py-2 rounded font-semibold ${esMovil ? 'flex-1 text-sm' : 'text-xs'}`}
              style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: (guardando || cargando) ? 0.6 : 1 }}>
              {guardando ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar muestreo
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Vista MÓVIL: una sección por pantalla, campos apilados (touch-friendly) ────
function VistaMovil({
  filas, cols, grupos, idx, setIdx, setDato, setMuestra, totalCol,
}: {
  filas: FilaCaptura[];
  cols: Columna[];
  grupos: { grupo: string; cols: Columna[] }[];
  idx: number;
  setIdx: (n: number) => void;
  setDato: (fi: number, key: string, val: string) => void;
  setMuestra: (fi: number, val: string) => void;
  totalCol: (key: string) => number;
}) {
  const f = filas[idx];
  if (!f) return null;
  const inputMovil: React.CSSProperties = {
    width: '4.5rem', textAlign: 'center', background: 'var(--surface-deep)', outline: 'none',
    color: 'var(--ink)', border: '1px solid var(--line)', borderRadius: 8, padding: '10px 8px', fontSize: '17px',
  };
  return (
    <div className="flex flex-col gap-3">
      {/* Selector de sección con progreso */}
      <div className="flex items-center justify-between gap-2 sticky top-0 z-10 py-2"
        style={{ background: 'var(--surface-base)' }}>
        <button onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0}
          className="px-3 py-2 rounded text-sm font-semibold"
          style={{ border: '1px solid var(--line)', color: idx === 0 ? 'var(--ink-faint)' : 'var(--ink)', opacity: idx === 0 ? 0.4 : 1 }}>
          ‹ Ant.
        </button>
        <div className="text-center">
          <div className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
            Lote {f.lote_num} · Sec. {f.seccion}
          </div>
          <div className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
            {idx + 1} de {filas.length} · {f.area_ha != null ? Number(f.area_ha).toFixed(2) : '—'} ha · {f.plantas != null ? f.plantas.toLocaleString('es-CR') : '—'} plantas
          </div>
        </div>
        <button onClick={() => setIdx(Math.min(filas.length - 1, idx + 1))} disabled={idx === filas.length - 1}
          className="px-3 py-2 rounded text-sm font-semibold"
          style={{ border: '1px solid var(--line)', color: idx === filas.length - 1 ? 'var(--ink-faint)' : 'var(--ink)', opacity: idx === filas.length - 1 ? 0.4 : 1 }}>
          Sig. ›
        </button>
      </div>

      {/* Muestra */}
      <label className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg"
        style={{ background: 'var(--surface-raised)', border: '1px solid var(--line-dim)' }}>
        <span className="text-sm font-medium" style={{ color: 'var(--ink-muted)' }}>Muestra</span>
        <input type="number" inputMode="numeric" value={f.muestra ?? ''} onChange={(e) => setMuestra(idx, e.target.value)} style={inputMovil} />
      </label>

      {/* Campos agrupados */}
      {grupos.map((g, gi) => (
        <div key={gi} className="rounded-lg overflow-hidden" style={{ border: '1px solid var(--line-dim)' }}>
          {g.grupo && (
            <div className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider"
              style={{ background: 'var(--surface-deep)', color: 'var(--emp-accent-txt)' }}>{g.grupo}</div>
          )}
          {g.cols.map((c) => (
            <label key={c.key} className="flex items-center justify-between gap-3 px-3 py-2"
              style={{ borderTop: '1px solid var(--line-dim)' }}>
              <span className="text-sm" style={{ color: 'var(--ink)' }}>{c.sub || c.label}</span>
              <input type="number" inputMode="numeric" value={f.datos[c.key] ?? ''}
                onChange={(e) => setDato(idx, c.key, e.target.value)} min={c.min} max={c.max} style={inputMovil} />
            </label>
          ))}
        </div>
      ))}

      {/* Mini-resumen de totales acumulados */}
      <div className="rounded-lg px-3 py-2 mt-1" style={{ background: 'var(--surface-deep)', border: '1px solid var(--line-dim)' }}>
        <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Totales acumulados (todas las secciones)</div>
        <div className="flex flex-wrap gap-1.5">
          {cols.map((c) => {
            const t = totalCol(c.key);
            return t > 0 ? (
              <span key={c.key} className="text-[11px] px-1.5 py-0.5 rounded font-mono"
                style={{ background: 'var(--surface-raised)', color: 'var(--ink-muted)', border: '1px solid var(--line-dim)' }}>
                {c.sub || c.label}: <b style={{ color: 'var(--ink)' }}>{t}</b>
              </span>
            ) : null;
          })}
        </div>
      </div>
    </div>
  );
}

const thFijo: React.CSSProperties = {
  padding: '4px 6px', fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--ink-muted)', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line-dim)',
  whiteSpace: 'nowrap', textAlign: 'left', background: 'var(--surface-raised)',
};
const thGrupo: React.CSSProperties = {
  padding: '3px 4px', fontSize: '9px', fontWeight: 700, textAlign: 'center',
  color: 'var(--emp-accent-txt)', borderBottom: '1px solid var(--line-dim)', borderRight: '1px solid var(--line-dim)',
};
const thCol: React.CSSProperties = {
  padding: '3px 4px', fontSize: '8.5px', fontWeight: 600, textAlign: 'center', verticalAlign: 'bottom',
  color: 'var(--ink-muted)', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line-dim)',
  maxWidth: '3.4rem', lineHeight: 1.15,
};
const tdFijo: React.CSSProperties = {
  padding: '3px 6px', borderRight: '1px solid var(--line-dim)', color: 'var(--ink)', whiteSpace: 'nowrap',
};
